import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import interactions from '~/components/page/Automation/v2/state/interactions';
import getActionsOnLowestLevel from '~/components/page/Automation/v2/components/Builder/utils/getActionsOnLowestLevel';
import type { ClientFlowAction } from '~/graphql/types.client';

type Params = {
  sourceId: string;
  targetId: string;
};

type UseCopyActionDataResult = {
  isCopyActionInteraction: boolean;
  isChoosingPathToConnect: boolean;
  isPastePreviewEdge: boolean;
  isPastingPhase: boolean;
  lowestLevelIds: Set<string>;
  copiedActionById: Map<string, ClientFlowAction>;
  copiedAction: ClientFlowAction | undefined;
};

const useCopyActionData = ({
  sourceId,
  targetId,
}: Params): UseCopyActionDataResult => {
  const currentInteraction = useRecoilValue(interactions);

  const isCopyActionInteraction = currentInteraction?.type === 'copyAction';

  const copiedActions = useMemo(
    () => (isCopyActionInteraction ? currentInteraction.copiedActions : []),
    [isCopyActionInteraction, currentInteraction],
  );

  const lowestLevelIds = useMemo(() => {
    const lowestLevel = getActionsOnLowestLevel(copiedActions);
    return new Set(lowestLevel.map(action => action.id));
  }, [copiedActions]);

  const copiedActionById = useMemo(
    () => new Map(copiedActions.map(action => [action.id, action])),
    [copiedActions],
  );

  const copiedAction = copiedActionById.get(sourceId);

  const isChoosingPathToConnect =
    isCopyActionInteraction &&
    currentInteraction.phase === 'choosingPathToConnect';

  const isPastePreviewEdge =
    isChoosingPathToConnect &&
    currentInteraction.pastePoint?.source === sourceId &&
    currentInteraction.pastePoint?.target === targetId;

  const isPastingPhase =
    isCopyActionInteraction && currentInteraction.phase === 'pasting';

  return {
    isCopyActionInteraction,
    isChoosingPathToConnect,
    isPastePreviewEdge,
    isPastingPhase,
    lowestLevelIds,
    copiedActionById,
    copiedAction,
  };
};

export default useCopyActionData;
